import React, { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import arePropsEqual from 'react-fast-compare';
import { useSelector } from 'react-redux';
import { Icon } from '@modulor/react';
import Card from '../../../components/Card/Card';
import Image from '../../../components/Image/Image';
import { LinkWithAnalytics } from '../../../components/Link/Link';
import { IconButtonWithAnalytics } from '../../../components/Button/IconButton/IconButton';
import FavoriteButton from '../../../components/Button/FavoriteButton/FavoriteButton';
import { ButtonWithAnalytics } from '../../../components/Button/Button';
import { ReactComponent as FallbackIcon } from '../../assets/icons/image-fallback.svg';
import { useIsDesktopLayout } from '../../../hooks/useIsDesktopLayout';
import { getGameDetailRoute } from '../../../utils/routes';
import { getGameImagePath } from '../../../utils/imagePath';
import { getDataTestAttrs } from '../../../utils/testing-locators';
import {
  gameImageSelector,
  randomGameIdSelector,
} from '../../../redux/reducers/app/configuration';
import {
  GAME_CARD,
  GAME_IMAGE,
  GAME_LINK,
  GAME_PLAY_BUTTON,
} from '../../../constants/dataTestIds';
import RandomGameCardContent from './RandomGameCardContent';
import styles from './GameCard.module.css';
import {
  ANALYTICS_SCHEMA_URLS,
  ANALYTICS_SCREEN_AREA_DETAILS,
} from '../../../constants/analytics';

const GameCard = ({
  game,
  routeState,
  isLoading,
  sectionIndex,
  eventActionPlay,
  eventActionPlayDemo,
  eventActionFavorite,
  className,
  index,
  analyticsData,
  additionalLayerData,
  withDisabledFavourite,
  ...props
}) => {
  const isDesktop = useIsDesktopLayout();
  const gameImagePath = useSelector(gameImageSelector);
  const history = useHistory();
  const randomGameId = useSelector(randomGameIdSelector);
  const { id, translationKey, provider } = game;

  const isRandomGame = randomGameId === id;

  const clickHandler = useCallback(() => {
    history.push(getGameDetailRoute(id), routeState);
  }, [history, id, routeState]);

  const commonAnalyticsData = {
    ...analyticsData,
    game_id: id,
    position_index: index,
    provider_id: provider,
    section_index: analyticsData?.section_index ?? sectionIndex ?? '',
  };
  const playAnalyticsData = {
    ...commonAnalyticsData,
    schema: ANALYTICS_SCHEMA_URLS.PLAY_GAME_CLICK,
  };
  const favoriteAnalyticsData = {
    ...commonAnalyticsData,
    event_detail: commonAnalyticsData.event_detail || '',
    schema: ANALYTICS_SCHEMA_URLS.ADD_FAVORITE_CLICK,
  };

  return (
    <Card
      isLoading={isLoading}
      className={classNames(styles.component_ig, className)}
      {...getDataTestAttrs(GAME_CARD, id)}
      {...props}
    >
      <Image
        src={getGameImagePath(gameImagePath, id, isDesktop)}
        alt={translationKey}
        fallback={
          !isRandomGame && (
            <div className={styles.fallback_ig}>
              <FallbackIcon className={styles.fallback_icon_ig} />
            </div>
          )
        }
        className={styles.background_ig}
        {...getDataTestAttrs(GAME_IMAGE)}
      />

      {isRandomGame ? (
        <RandomGameCardContent
          translationKey={translationKey}
          id={id}
          additionalLayerData={additionalLayerData}
          eventActionPlay={eventActionPlay}
          index={index}
          sectionIndex={sectionIndex}
          provider={provider}
          routeState={routeState}
          analyticsData={{
            ...playAnalyticsData,
            screen_area_detail:
              ANALYTICS_SCREEN_AREA_DETAILS.CATEGORY_RANDOM_GAME,
          }}
        />
      ) : isDesktop ? (
        <>
          <div className={styles.overlay_ig}>
            <div className={styles.heading_ig}>
              {!withDisabledFavourite && (
                <FavoriteButton
                  game={game}
                  className={styles.favorite_ig}
                  dataLayerEventData={{
                    ...(additionalLayerData || {}),
                    eventAction: eventActionFavorite,
                    game_id: id,
                    position_index: index,
                    section_index: sectionIndex,
                    provider_id: provider,
                  }}
                  analyticsData={favoriteAnalyticsData}
                />
              )}
            </div>
            <IconButtonWithAnalytics
              className={styles.play_ig}
              onClick={clickHandler}
              dataLayerEventData={{
                ...(additionalLayerData || {}),
                eventAction: eventActionPlay,
                game_id: id,
                position_index: index,
                section_index: sectionIndex,
                provider_id: provider,
              }}
              analyticsData={playAnalyticsData}
              {...getDataTestAttrs(GAME_PLAY_BUTTON)}
            >
              <Icon name="play_filled" className={styles['play-icon_ig']} />
            </IconButtonWithAnalytics>
          </div>
          {isLoading ||
            (!isRandomGame ? (
              <div className={styles.title_ig}>
                <LinkWithAnalytics
                  to={{ pathname: getGameDetailRoute(id), state: routeState }}
                  className={styles.title_link_ig}
                  dataLayerEventData={{
                    ...(additionalLayerData || {}),
                    eventAction: eventActionPlay,
                    game_id: id,
                    position_index: index,
                    section_index: sectionIndex,
                    provider_id: provider,
                  }}
                  analyticsData={playAnalyticsData}
                  {...getDataTestAttrs(GAME_LINK)}
                >
                  {!isRandomGame ? translationKey : null}
                </LinkWithAnalytics>
              </div>
            ) : (
              <span className={styles.title_ig} />
            ))}
        </>
      ) : (
        <>
          {!withDisabledFavourite && (
            <FavoriteButton
              game={game}
              className={styles.favorite_ig}
              dataLayerEventData={{
                ...(additionalLayerData || {}),
                eventAction: eventActionFavorite,
                game_id: id,
                position_index: index,
                section_index: sectionIndex,
                provider_id: provider,
              }}
              analyticsData={favoriteAnalyticsData}
            />
          )}
          <ButtonWithAnalytics
            onClick={clickHandler}
            className={styles.mobile_link_ig}
            dataLayerEventData={{
              ...(additionalLayerData || {}),
              eventAction: eventActionPlay,
              game_id: id,
              position_index: index,
              section_index: sectionIndex,
              provider_id: provider,
            }}
            analyticsData={playAnalyticsData}
            {...getDataTestAttrs(GAME_LINK)}
          />
          {isLoading ||
            (!isRandomGame ? (
              <div className={styles.title_ig}>
                <LinkWithAnalytics
                  to={{ pathname: getGameDetailRoute(id), state: routeState }}
                  className={styles.title_link_ig}
                  dataLayerEventData={{
                    ...(additionalLayerData || {}),
                    eventAction: eventActionPlay,
                    game_id: id,
                    position_index: index,
                    section_index: sectionIndex,
                    provider_id: provider,
                  }}
                  analyticsData={playAnalyticsData}
                  {...getDataTestAttrs(GAME_LINK)}
                >
                  {!isRandomGame ? translationKey : null}
                </LinkWithAnalytics>
              </div>
            ) : (
              <span className={styles.title_ig} />
            ))}
        </>
      )}
    </Card>
  );
};

GameCard.propTypes = {
  game: PropTypes.shape({
    id: PropTypes.string,
    translationKey: PropTypes.string,
  }),
  routeState: PropTypes.shape({}),
  eventActionPlay: PropTypes.string,
  eventActionPlayDemo: PropTypes.string,
  eventActionFavorite: PropTypes.string,
  sectionIndex: PropTypes.number,
  index: PropTypes.number,
  additionalLayerData: PropTypes.shape({}),
  withDisabledFavourite: PropTypes.bool,
  analyticsData: PropTypes.shape({
    screen_name: PropTypes.string,
    screen_area: PropTypes.string,
    screen_area_detail: PropTypes.string,
    event_detail: PropTypes.string,
  }),
};

GameCard.defaultProps = {
  game: {
    demoModeAvailable: false,
  },
  routeState: {},
  isLoading: false,
  additionalLayerData: {},
  withDisabledFavourite: false,
  analyticsData: {},
};

export default memo(GameCard, arePropsEqual);
